import {useMantineTheme} from '@mantine/core';
import {Box} from 'src/design/atoms/Box';
import type {OnChangeFn} from '@tanstack/react-table';
import fileDownload from 'js-file-download';
import type {
  MRT_ColumnFiltersState,
  MRT_RowSelectionState,
  MRT_SortingState,
  MRT_VisibilityState,
} from 'mantine-react-table';
import React, {useImperativeHandle, useState} from 'react';
import type {
  GetPersonnelListRequestV2,
  PersonnelSimpleResponse,
  UserTypeEnum,
} from 'src/api/ProactApiClient';
import {ModuleEnum, PermissionOperationEnum} from 'src/api/ProactApiClient';
import {TableRowActions} from 'src/design/molecules/TableRowActions';
import {TableToolbar} from 'src/design/molecules/TableToolbar';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {PermissionGuard} from 'src/app/security/authorization/PermissionGuard';
import {CheckboxRow} from 'src/design/molecules/CheckboxRow';
import {
  useExportPersonnelMutation,
  useGetPersonnelListV2Query,
} from 'src/redux/queries/personnel';
import {usePersonnelPermissions} from 'src/shared/auth/permissions/usePersonnelPermissions';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import {IsPersonnelFlagged} from 'src/app/personnel/personnelHelpers';
import {useVendorPersonnelColumns} from './hooks/useVendorPersonnelColumns';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export interface VendorPersonnelTableProps {
  identifier: string;
  userType: UserTypeEnum;
  vendorId?: number;
  defaultSorting?: MRT_SortingState;
  defaultColumnFilters?: MRT_ColumnFiltersState;
  defaultColumnVisibility?: MRT_VisibilityState;
  actionBarPreNode?: React.ReactNode;
  enableRowSelection?: boolean;
  rowSelection?: MRT_RowSelectionState;
  onRowSelectionChange?: OnChangeFn<MRT_RowSelectionState>;
  onRowClick?: (row: PersonnelSimpleResponse) => void;
  disableActiveFilter?: boolean;
  disablePendingActionFilter?: boolean;
}

export interface VendorPersonnelTableRef {
  refetch: () => void;
}

export const VendorPersonnelTable = React.forwardRef<
  VendorPersonnelTableRef,
  VendorPersonnelTableProps
>(
  (
    {
      identifier,
      userType,
      vendorId,
      defaultSorting,
      defaultColumnFilters,
      defaultColumnVisibility,
      actionBarPreNode,
      enableRowSelection = false,
      rowSelection = {},
      onRowSelectionChange,
      onRowClick,
      disableActiveFilter = false,
      disablePendingActionFilter = false,
    },
    ref
  ) => {
    const {setAlert} = useAlert();
    const navigate = useAppNavigate();
    const theme = useMantineTheme();
    const [isPendingActionFilterChecked, setIsPendingActionFilterChecked] =
      useState<boolean>(false);

    const personnelPerms = usePersonnelPermissions();

    const columns = useVendorPersonnelColumns({
      disableActiveFilter,
    });

    const tableState = useTableState({
      identifier,
      columns,
      defaultColumnFilters: defaultColumnFilters ?? [],
      defaultColumnVisibility: defaultColumnVisibility ?? {
        jobCode: false,
        'vendor.id': false,
        updatedAt: false,
      },
      defaultDensity: 'xs',
      defaultPagination: {
        pageIndex: 0,
        pageSize: 20,
      },
      defaultSorting: defaultSorting ?? [{id: 'lastName', desc: false}],
      defaultShowColumnFilters: true,
    });

    const {start, length, filters, filterFns, sorting} = tableState;

    const request: GetPersonnelListRequestV2 = {
      start,
      length,
      filters,
      filterFns,
      sorting,
      userTypes: [userType],
      vendorId,
      hasPendingAction: isPendingActionFilterChecked,
    };

    const {data, isLoading, isError, isFetching, refetch} =
      useGetPersonnelListV2Query(request, {refetchOnMountOrArgChange: true});

    useImperativeHandle(ref, () => ({
      refetch,
    }));

    const rows = data?.data ?? [];
    const totalCount = data?.totalRecords ?? 0;

    const [exportPersonnelMutation] = useExportPersonnelMutation();

    const exportPersonnel = async () => {
      const exportRequest: GetPersonnelListRequestV2 = {
        ...request,
        length: 1000000,
      };
      const response = await exportPersonnelMutation(exportRequest).unwrap();
      fileDownload(
        response!.data,
        `personnel_for_vendor_${vendorId}_export.csv`
      );
      setAlert('Your file has been successfully exported. ', 'success');
    };

    const handlePendingActionFilter = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      setIsPendingActionFilterChecked(event.target.checked);
    };

    const {table} = useTable({
      ...tableState,
      rowSelection,
      tableOptions: {
        enableRowSelection,
        enableRowActions: personnelPerms.view,
        manualSorting: true,
        manualFiltering: true,
        manualPagination: true,
        columns,
        data: rows,
        onRowSelectionChange,
        getRowId: row => String(row.id),
        mantineTableBodyCellProps: ({row}) => ({
          style: {
            backgroundColor: IsPersonnelFlagged(row.original)
              ? theme.colors.orange[1]
              : 'white',
          },
        }),
        renderRowActions: ({row}) => (
          <TableRowActions>
            {personnelPerms.view && (
              <TableRowActions.View
                onClick={() => {
                  if (onRowClick) {
                    onRowClick(row.original);
                  } else {
                    navigate(
                      PathUtility.VendorPersonnelView(
                        String(row.original.id),
                        String(row.original.vendorId),
                        'overview'
                      )
                    );
                  }
                }}
              />
            )}
          </TableRowActions>
        ),
        renderTopToolbarCustomActions: () => (
          <TableToolbar>
            {!disablePendingActionFilter && (
              <TableToolbar.Filters>
                <Box mt="sm">
                  <CheckboxRow
                    label="Has Pending Action"
                    checked={isPendingActionFilterChecked}
                    onChange={handlePendingActionFilter}
                  />
                </Box>
              </TableToolbar.Filters>
            )}
            <TableToolbar.Actions>
              {actionBarPreNode}
              <PermissionGuard
                module={ModuleEnum.Personnel}
                operation={PermissionOperationEnum.Export}>
                <TableToolbar.Action
                  variant="export"
                  onClick={() => exportPersonnel()}
                />
              </PermissionGuard>
            </TableToolbar.Actions>
          </TableToolbar>
        ),
      },
      totalCount,
      isError,
      isLoading,
      isFetching,
      onRefresh: refetch,
    });

    return <>{table}</>;
  }
);
